import { session } from '$lib/auth/session.svelte';
import { tokens } from '$lib/auth/tokens';
import { i18n } from '$lib/i18n/i18n.svelte';
import { flushOutbox, type FlushResult } from '$lib/offline/outbox';
import { network } from './network.svelte';
import { notifications } from './notifications';
import { sync } from './sync.svelte';
import { toast } from './toast.svelte';

let retryTimer: ReturnType<typeof setTimeout> | undefined;

/**
 * Coming back online or to the app, or pulling to refresh: sends queued ticks, reads the household again (its
 * departments and stores may have changed), then lets the pages reload their data and fetches new in-app messages.
 * Says how many ticks went out, since only ticks made offline are still queued here.
 * Resolves with whether everything reloaded; false when offline or the household could not be read.
 */
export async function resync(): Promise<boolean> {
	if (!network.online || session.status !== 'ready') return false;
	if (!tokens.get()) await tokens.refresh();
	const { sent } = await sendQueued();
	if (sent) toast.show(i18n.t.layout.backOnline(sent), 'success');
	void notifications.refresh();
	const household = await session.refreshHousehold().then(
		() => true,
		() => false
	);
	return (await sync.reload()) && household;
}

/**
 * Sends the offline outbox. Says so when the server refused ticks (the pages then reload to show what
 * was saved) and tries again when the server asked us to wait.
 */
export async function sendQueued(): Promise<FlushResult> {
	const result = await flushOutbox();
	if (result.rejected.length) {
		toast.show(i18n.t.shopping.checksRejected, 'warning');
		sync.bump();
	}
	if (result.retryAt) retryAt(result.retryAt);
	return result;
}

function retryAt(time: number) {
	clearTimeout(retryTimer);
	retryTimer = setTimeout(() => void resync(), Math.max(0, time - Date.now()));
}
